import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, ButtonGroup, Chip, Paper, Stack, Typography } from '@mui/material';
import { getDownloadURL, ref } from 'firebase/storage';
import { useQuery } from '@tanstack/react-query';
import { storage } from '../../../firebase/firebase';
import { SiteDraft } from '../../../sitePublishing/types';
import { Appointment } from '../../../types';
import BookNowDialog from '../../../components/booking/BookNowDialog';
import {
  fetchAllEmployees,
  fetchServices,
} from '../../../firebase/firebase-delegate';
import { useTenant } from '../../../tenancy/TenantContext';
import {
  TenantSiteRenderer,
  TenantSiteEditorSectionId,
  TenantSiteRendererModel,
} from '../../../components/tenantSite/TenantSiteRenderer';
import '../../../components/tenantSite/tenant-site.css';
import { SitePresentationSettings } from './sitePresentationSettings';

export type PreviewViewport = 'desktop' | 'mobile';
const EMPTY_ASSET_URLS: Record<string, string | undefined> = {};

const contrastColor = (hex: string): string => {
  const value = hex.replace('#', '');
  const channels = [0, 2, 4].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16));
  const luminance = (channels[0] * 299 + channels[1] * 587 + channels[2] * 114) / 1000;
  return luminance > 150 ? '#111827' : '#ffffff';
};

const resolveAsset = async (path: string): Promise<string | undefined> => {
  if (!path) return undefined;
  if (/^https:\/\//i.test(path)) return path;
  try {
    return await getDownloadURL(ref(storage, path));
  } catch {
    return undefined;
  }
};

export type SiteDraftPreviewProps = {
  draft: SiteDraft;
  presentationSettings: SitePresentationSettings;
  viewport?: PreviewViewport;
  onViewportChange?: (viewport: PreviewViewport) => void;
  selectedSectionId?: TenantSiteEditorSectionId;
  onSelectSection?: (sectionId: TenantSiteEditorSectionId) => void;
  selectedGalleryIndex?: number;
  onSelectGalleryImage?: (index: number) => void;
  showControls?: boolean;
  // Preview mode: show the "Preview only" banner visitors see.
  comingSoon?: boolean;
};

const SiteDraftPreview: React.FC<SiteDraftPreviewProps> = ({
  draft,
  presentationSettings,
  viewport: controlledViewport,
  onViewportChange,
  selectedSectionId,
  onSelectSection,
  selectedGalleryIndex,
  onSelectGalleryImage,
  showControls = true,
  comingSoon = false,
}) => {
  const { tenantId } = useTenant();
  const [internalViewport, setInternalViewport] = useState<PreviewViewport>('desktop');
  const [previewAppointment, setPreviewAppointment] = useState<Appointment | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const viewport = controlledViewport ?? internalViewport;
  const setViewport = (nextViewport: PreviewViewport) => {
    setInternalViewport(nextViewport);
    onViewportChange?.(nextViewport);
  };
  const servicesQuery = useQuery({
    queryKey: ['services', tenantId],
    queryFn: () => fetchServices(tenantId),
  });
  const employeesQuery = useQuery({
    queryKey: ['employees', tenantId],
    queryFn: () => fetchAllEmployees(tenantId),
  });
  const assetPaths = useMemo(() => Array.from(new Set([
    draft.assets.hero,
    draft.homepage.heroMediaType === 'video' ? draft.assets.heroVideo : undefined,
    draft.assets.logo,
    draft.assets.heroLogo,
    ...draft.homepage.gallery.map((item) => item.image),
    ...(servicesQuery.data ?? []).map((service) => service.imagePath),
    ...(employeesQuery.data ?? []).map((employee) => employee.profilePictureImagePath),
  ].filter((path): path is string => Boolean(path)))).sort(), [
    draft.assets.hero,
    draft.homepage.heroMediaType,
    draft.assets.heroVideo,
    draft.assets.logo,
    draft.assets.heroLogo,
    draft.homepage.gallery,
    servicesQuery.data,
    employeesQuery.data,
  ]);
  const assetsQuery = useQuery({
    queryKey: ['site-preview-assets', tenantId, assetPaths],
    queryFn: async () => {
      const resolved = await Promise.all(assetPaths.map(async (path) => [path, await resolveAsset(path)] as const));
      return Object.fromEntries(resolved);
    },
    enabled: assetPaths.length > 0,
  });
  const assetUrls = assetsQuery.data ?? EMPTY_ASSET_URLS;
  const previewServices = useMemo(
    () => (servicesQuery.data ?? []).filter((service) =>
      (!('active' in service) || service.active !== false) &&
      service.title.trim().length > 0 &&
      Number.isFinite(service.price) && service.price >= 0 &&
      Number.isFinite(service.time) && service.time > 0
    ),
    [servicesQuery.data]
  );
  const assignedEmployeeIds = useMemo(
    () => new Set(previewServices.flatMap((service) => service.employees ?? [])),
    [previewServices]
  );
  const model = useMemo<TenantSiteRendererModel>(() => ({
    tenantId,
    config: {
      theme: draft.theme,
      business: draft.business,
      homepage: {
        ...draft.homepage,
        gallery: draft.homepage.gallery.map((item) => ({
          ...item,
          imageUrl: assetUrls[item.image],
        })),
      },
      assets: {
        logoUrl: draft.assets.logo ? assetUrls[draft.assets.logo] : undefined,
        heroUrl: draft.assets.hero ? assetUrls[draft.assets.hero] : undefined,
        heroVideoUrl: draft.assets.heroVideo ? assetUrls[draft.assets.heroVideo] : undefined,
        heroLogoUrl: draft.assets.heroLogo
          ? assetUrls[draft.assets.heroLogo]
          : draft.assets.logo ? assetUrls[draft.assets.logo] : undefined,
      },
      socials: draft.socials,
      sections: draft.sections,
      booking: draft.booking,
    },
    services: previewServices.map((service) => ({
      id: service.id,
      title: service.title.trim(),
      description: service.description?.trim(),
      price: service.price,
      priceVaries: service.priceVaries === true,
      durationMinutes: service.time,
      imageUrl: service.imagePath ? assetUrls[service.imagePath] : undefined,
      showOnHomepage: service.showOnHomepage ?? Boolean(service.imagePath),
    })),
    staff: (employeesQuery.data ?? [])
      .filter((employee) =>
        employee.active !== false &&
        employee.bookable !== false &&
        assignedEmployeeIds.has(employee.id)
      )
      .map((employee) => ({
        id: employee.id,
        firstName: employee.firstName,
        displayName: (employee.firstName + ' ' + employee.lastName).trim(),
        bio: presentationSettings.showEmployeeBio ? employee.bio : undefined,
        imageUrl: presentationSettings.showEmployeeProfilePhoto && employee.profilePictureImagePath
          ? assetUrls[employee.profilePictureImagePath]
          : undefined,
        socials: presentationSettings.showEmployeeSocials ? employee.socials : undefined,
      })),
    resourcePlural: presentationSettings.resourcePlural,
  }), [
    tenantId,
    draft,
    assetUrls,
    previewServices,
    employeesQuery.data,
    assignedEmployeeIds,
    presentationSettings,
  ]);

  useEffect(() => {
    const existing = document.querySelector('meta[name="robots"]');
    const previous = existing?.getAttribute('content');
    const meta = existing ?? document.head.appendChild(document.createElement('meta'));
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', 'noindex,nofollow,noarchive');
    return () => {
      if (existing && typeof previous === 'string') existing.setAttribute('content', previous);
      if (!existing) meta.remove();
    };
  }, []);

  useEffect(() => {
    if (!selectedSectionId || selectedSectionId === 'style' || selectedSectionId === 'seo') return;
    const selected = previewRef.current?.querySelector(
      `[data-site-editor-section="${selectedSectionId}"]`
    );
    const canvas = canvasRef.current;
    if (!(selected instanceof HTMLElement) || !canvas) return;
    const selectedBounds = selected.getBoundingClientRect();
    const canvasBounds = canvas.getBoundingClientRect();
    const nextTop = canvas.scrollTop + selectedBounds.top - canvasBounds.top -
      Math.max(0, (canvas.clientHeight - selectedBounds.height) / 2);
    canvas.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
  }, [selectedSectionId]);

  return (
    <Stack spacing={1.5} alignItems="center" width="100%">
      {showControls && (
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" width="100%">
          <Chip label="Public-site preview" color="warning" />
          <ButtonGroup size="small" aria-label="Preview viewport">
            <Button
              variant={viewport === 'desktop' ? 'contained' : 'outlined'}
              onClick={() => setViewport('desktop')}
            >
              Desktop
            </Button>
            <Button
              variant={viewport === 'mobile' ? 'contained' : 'outlined'}
              onClick={() => setViewport('mobile')}
            >
              Mobile
            </Button>
          </ButtonGroup>
        </Stack>
      )}
      <Paper
        ref={previewRef}
        id="preview"
        elevation={0}
        sx={{
          width: viewport === 'mobile' ? 390 : '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          transition: 'width 180ms ease',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          bgcolor: '#e8ebe9',
          boxShadow: '0 20px 55px rgba(15, 23, 42, .12)',
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            height: 34,
            px: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.7,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: '#f8faf9',
          }}
        >
          {['#ef7d73', '#e7bd58', '#6fc18c'].map((color) => (
            <Box key={color} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {draft.business.displayName}
          </Typography>
        </Box>
        <Box
          ref={canvasRef}
          sx={{ maxHeight: 'calc(100vh - 285px)', minHeight: 480, overflow: 'auto', bgcolor: draft.theme.backgroundColor }}
        >
          {draft.experience === 'booking_only' ? (
            <Box
              sx={{
                minHeight: 620,
                height: 'calc(100vh - 285px)',
                maxHeight: 820,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: draft.theme.backgroundColor,
                color: draft.theme.textColor,
              }}
            >
              <Box
                data-site-editor-section="branding"
                sx={{
                  minHeight: 68,
                  px: viewport === 'mobile' ? 2.25 : 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  bgcolor: draft.theme.primaryColor,
                  color: contrastColor(draft.theme.primaryColor),
                  borderBottom: '1px solid',
                  borderColor: 'rgba(255,255,255,.16)',
                }}
              >
                {model.config.assets.logoUrl ? (
                  <Box
                    component="img"
                    src={model.config.assets.logoUrl}
                    alt=""
                    sx={{ width: 44, height: 44, objectFit: 'contain', flex: '0 0 auto' }}
                  />
                ) : null}
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={800} noWrap>{draft.business.displayName}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.72 }}>Online booking</Typography>
                </Box>
              </Box>
              <Box
                data-site-editor-section="booking"
                sx={{
                  position: 'relative',
                  flex: 1,
                  minHeight: 0,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    zIndex: 2,
                  },
                }}
                aria-label="Booking flow preview"
              >
                <BookNowDialog
                  appointment={previewAppointment}
                  handleAppointment={setPreviewAppointment}
                  services={servicesQuery.data ?? []}
                  handleCloseBooking={() => undefined}
                  enabledCustomerSMS={false}
                  enabledEmployeeSMS={false}
                  enabledPayments={false}
                  requirePaymentForBooking={false}
                  minimumBookingNoticeHours={1}
                  employeesOverride={employeesQuery.data ?? []}
                  showPrices={draft.booking.showPrices}
                  showEmployeePicker={draft.booking.showEmployeePicker}
                  showCloseAction={false}
                />
              </Box>
            </Box>
          ) : (
            <TenantSiteRenderer
              model={model}
              adminUrl="#preview"
              bookingUrl="#preview"
              appointmentUrl="#preview"
              preview
              previewViewport={viewport}
              comingSoon={comingSoon}
              editorMode={selectedSectionId && onSelectSection ? {
                selectedSectionId,
                onSelectSection,
                selectedGalleryIndex,
                onSelectGalleryImage,
              } : undefined}
            />
          )}
        </Box>
      </Paper>
      {showControls && (
        <Typography variant="caption" color="text.secondary">
          This is the same renderer used by the published Next site.
        </Typography>
      )}
    </Stack>
  );
};

export default SiteDraftPreview;
